import withStore, {
  TPresentable,
  TLoadingAwarePresenter
} from "../../core/presenter/withStore";
import Application from "../../Application";
import ShowSuccessMessageInteractor from "../../core/interactor/notifications/ShowSuccessMessageInteractor";
import CreateAttachmentFileInteractor from "../../caseLawDocuments/interactor/attachments/CreateAttachmentFileInteractor";
import RemoveLegalViewAttachmentInteractor from "../interactor/RemoveLegalViewAttachmentInteractor";
import GetAttachmentsForLegalViewsInteractor from "../interactor/GetAttachmentsForLegalViewsInteractor";
import CreateLegalViewAttachmentInteractor from "../interactor/CreateLegalViewAttachmentInteractor";
import { IOrganisationIndex } from "../../organisations/model/OrganisationIndex";
import { IOrganisation } from "../../organisations/model/Organisation";
import LegalViewPresentationModel, {
  TLegalViewPresentationModel
} from "./model/LegalViewPresentationModel";
import { ILegalView } from "../model/LegalView";
import { IUser } from "../../user/model/User";
import UpdateLegalViewInteractor from "../interactor/UpdateLegalViewInteractor";
import GetLegalViewTypeIndexDataInteractor from "../interactor/GetLegalViewTypeIndexDataInteractor";
import GetOrganisationLegalViewTypesInteractor from "../../organisations/interactors/GetOrganisationLegalViewTypesInteractor";
import { IAttachmentType } from "../../attachmentType/model/AttachmentType";
import Attachment, {
  IAttachment
} from "../../caseLawDocuments/model/Attachment";
import { IGenericCodebookItem } from "../../codebooks/model/GenericCodebookItem";
import AttachmentTypePresentationModel, {
  TAttachmentTypePresentationModel
} from "../../organisations/presenter/model/AttachmentTypePresentationModel";
import AttachmentPresentationModel, {
  TAttachmentPresentationModel
} from "../../caseLawDocuments/presenter/model/AttachmentPresentationModel";
import { ContentTypes } from "../../caseLawDocuments/common/Constants";
import GetAllOrganisationAttachmentTypesInteractor from "../../organisations/interactors/GetAllOrganisationAttachmentTypesInteractor";
import SummaryTranslation from "../../core/model/translations/SummaryTranslation";

import OrganisationIndexPresentationModel, {
  TOrganisationIndexPresentationModel
} from "../../organisations/presenter/model/OrganisationIndexPresentationModel";
import DocumentTypeIndexCategoryPresentationModel, {
  TDocumentTypeIndexCategoryPresentationModel
} from "../../documentTypes/presenter/DocumentTypeIndexCategoryPresentationModel";
import {
  IDocumentType,
  TDocumentType
} from "../../documentTypes/model/DocumentType";
import { IDocumentTypeIndexCategory } from "../../documentTypes/model/DocumentTypeIndexCategory";
import { TOrganisationIndexGroupPresentationModel } from "../../organisations/presenter/model/OrganisationIndexGroupPresentationModel";
import PublishLegalViewInteractor from "../interactor/PublishLegalViewInteractor";
import ArchiveLegalViewInteractor from "../interactor/ArchiveLegalViewInteractor";
import CreateAttachmentInteractor from "../../caseLawDocuments/interactor/attachments/CreateAttachmentInteractor";
import GetAllIndexTypeAttributesInteractor from "../../indexTypes/interactors/GetAllIndexTypeAttributesInteractor";
import { IIndexTypeAttribute } from "../../indexTypes/model/IndexTypeAttribute";
import IndexAttributeData, {
  IIndexAttributeData
} from "../../indexTypes/model/IndexAttributeData";
import CreateIndexAttributeDataInteractor from "../../indexTypes/interactors/CreateIndexAttributeDataInteractor";
import GetIndexAttributeDataByIndexInteractor from "../../indexTypes/interactors/GetIndexAttributeDataByIndexInteractor";
import ShowWarningMessageInteractor from "../../core/interactor/notifications/ShowWarningMessageInteractor";

export interface TLegalViewUpdatePresenter extends TLoadingAwarePresenter {
  legalView: TLegalViewPresentationModel;
  organisations: IOrganisation[];
  legalViewTypes: TDocumentType[];
  legalViewIndexes: TOrganisationIndexPresentationModel[];
  indexHierarchyList: string[][];
  user?: IUser;
  legalViewTypeId: number;
  selectedOrganisationId: number;
  selectedIndex: TOrganisationIndexPresentationModel[][];
  organisationIndexes: TDocumentTypeIndexCategoryPresentationModel[];
  attachmentTypes: TAttachmentTypePresentationModel[];
  attachmentContentTypes: IGenericCodebookItem[];
  attachmentList: TAttachmentPresentationModel[];
  uploadedFile: any;
  deletedAttachments: IAttachment[];
  attachmentType: string;
  selectedParentGroupIdList: number[];
  selectedSecondGroupIdList: number[];
  parentGroupList: TOrganisationIndexGroupPresentationModel[][];
  secondGroupList: TOrganisationIndexGroupPresentationModel[][];
  invalidatedLegalViews: TLegalViewPresentationModel[];
  publishedLegalViews: any[];
  invalidatePublishedLegalViewsVisible: boolean;
  selectedIndexes: TOrganisationIndexPresentationModel[][];
  anonimisedUploadedFile: any;
  attachmentModalVisible: boolean;
  selectedIndexForAttribute?: IOrganisationIndex;
  indexTypeAttributes?: IIndexTypeAttribute[];
  indexAttributeDataList?: IIndexAttributeData[];
  indexDataModalVisible: boolean;
  previousSelectedIds?: number[];
  decisionTypes: IGenericCodebookItem[];
}

export interface ILegalViewUpdatePresenter
  extends TLegalViewUpdatePresenter,
    TPresentable {
  loadLegalView(legalViews: ILegalView): void;
  loadReferenceValues(
    legalViewTypes: IDocumentType[],
    organisations: IOrganisation[],
    attachmentTypes: IAttachmentType[],
    attachments: IAttachment[],
    attachmentContentTypes: IGenericCodebookItem[]
  ): void;
  loadAttachments(attachmentList: IAttachment[]): void;
  loadLegalViewIndexes(legalViewIndexes: IOrganisationIndex[]);
  showLegalViewDetailsPage(id: number): void;
  updateLegalView(item: any): void;
  setLegalViewTypeId(legalViewTypeId: number): void;
  setOrganisationId(organisationId: number): void;
  createAttachment(attachment: IAttachment): void;
  setFileValue(file): void;
  updateAttachment(attachment: any): void;
  getAttachments(legalViews: ILegalView): void;
  setAttachmentType(attachmentType: string): void;
  onUpdate(legalView: ILegalView);
  canEdit(organisationId: number): void;
  publishLegalView(): void;
  archiveLegalView(): void;
  setParentGroupId(selectedSecondGroupId: number, index: number): void;
  setSecondGroupId(selectedSecondGroupId: number, index: number): void;
  setLegalViewTypeCode(caseDecisionTypeId: number): void;
  setAnonymisedFileValue(file: any): void;
  removeAttachment(id: number): void;
  openIndexDataModal(index: IOrganisationIndex, selectedIds: number[]): void;
  closeIndexDataModal(): void;
  setAttributesData(
    indexTypeAttribute: IIndexTypeAttribute,
    attributeData: string
  ): void;
  saveAttributeData(): void;
}

const defaultState: TLegalViewUpdatePresenter = {
  legalView: {} as TLegalViewPresentationModel,
  organisations: [],
  legalViewTypes: [],
  legalViewIndexes: [],
  indexHierarchyList: [],
  legalViewTypeId: NaN,
  selectedOrganisationId: NaN,
  selectedIndex: [],
  organisationIndexes: [],
  attachmentContentTypes: [],
  attachmentTypes: [],
  attachmentList: [],
  uploadedFile: null,
  deletedAttachments: [],
  attachmentType: "",
  selectedParentGroupIdList: [],
  selectedSecondGroupIdList: [],
  parentGroupList: [],
  secondGroupList: [],
  invalidatedLegalViews: [],
  publishedLegalViews: [],
  invalidatePublishedLegalViewsVisible: false,
  selectedIndexes: [],
  anonimisedUploadedFile: undefined,
  attachmentModalVisible: false,
  decisionTypes: [],
  indexAttributeDataList: [],
  indexDataModalVisible: false
};

const LegalViewUpdatePresenter = withStore<
  ILegalViewUpdatePresenter,
  TLegalViewUpdatePresenter
>(({ application, store, loader, translate }): ILegalViewUpdatePresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<
    TLegalViewUpdatePresenter & TLoadingAwarePresenter
  >();

  const loadLegalView = (item: ILegalView) => {
    const { organisations, legalViewTypes } =
      _store.getState<TLegalViewUpdatePresenter>();

    const legalViewType = legalViewTypes?.find(
      (value) => value.id === item.documentTypeId
    );
    const organisation = organisations?.find(
      (value) => value.id === item.organisationId
    );
    legalViewType && setLegalViewTypeCode(legalViewType.id);
    organisation && setOrganisationId(organisation.id);
    legalViewType && setLegalViewTypeId(legalViewType.id);
    const selectedLanguage = _store.getState<any>().selectedLanguage;
    let summaryTranslation = item.summaryTranslations.find(
      (item) => item.languageId === selectedLanguage.id
    )?.summaryTranslation;
    const summary = summaryTranslation ? summaryTranslation : item.summary;
    const legalView = LegalViewPresentationModel(
      item,
      legalViewType,
      organisation,
      summary
    );
    _store.update({
      legalView
    });
    loader.stop("legalViewLoader");
  };
  const loadReferenceValues = (
    legalViewTypes: IDocumentType[],
    organisations: IOrganisation[],
    attachmentTypesList: IAttachmentType[],
    attachmentList: IAttachment[],
    attachmentContentTypes: IGenericCodebookItem[]
  ) => {
    const user = _store.getState<any>().user;
    let attachmentTypes = (attachmentTypesList || []).map((item) => {
      const contentType = attachmentContentTypes?.find(
        (value) => value.id === item.attachmentContentTypeId
      );
      return AttachmentTypePresentationModel(item, contentType);
    });
    _store.update({
      legalViewTypes,
      organisations,
      attachmentTypes,
      attachmentContentTypes,
      user
    });
    loadAttachments(attachmentList);
  };

  const loadLegalViewIndexes = (legalViewIndexes: IOrganisationIndex[]) => {
    const { selectedLanguage } = _store.getState<any>();
    const data = legalViewIndexes.map((item) =>
      OrganisationIndexPresentationModel(
        item,
        undefined,
        undefined,
        undefined,
        undefined,
        selectedLanguage
      )
    );
    _store.update({
      legalViewIndexes: data,
      selectedIndex: data
    });
    prepareHierarchyList();
  };

  const loadAttachments = (attachments: IAttachment[]) => {
    const { attachmentTypes } = _store.getState<TLegalViewUpdatePresenter>();
    let attachmentList = (attachments || []).map((item) => {
      const attachmentType = attachmentTypes?.find(
        (value) => value.id === item.attachmentTypeId
      );
      return AttachmentPresentationModel(item, attachmentType);
    });
    _store.update({
      attachmentList
    });
    loader.stop("legalViewLoader");
  };

  const findParent = (group, groupList, indexHierarchyList) => {
    if (group?.parentId) {
      let g = groupList.find((g) => g.id === group?.parentId);
      if (g) {
        indexHierarchyList.push(g.name);
        return findParent(g, groupList, indexHierarchyList);
      }
    } else if (group?.organisationIndexCategoryId) {
      indexHierarchyList.push(group?.categoryName);
    }
    return indexHierarchyList;
  };

  const prepareHierarchyList = () => {
    let indexHierarchyList = state.indexHierarchyList;
    const { legalViewIndexes } = _store.getState<TLegalViewUpdatePresenter>();

    legalViewIndexes.forEach((index) => {
      let indexHierarchy: string[] = [];
      let firstGroup = index.groups.find(
        (g) => g.id === index.organisationGroupId
      );
      indexHierarchy.push(index.name);
      indexHierarchy.push(firstGroup?.name ?? "");
      indexHierarchy = findParent(firstGroup, index.groups, indexHierarchy);
      indexHierarchyList.push(indexHierarchy);
    });

    _store.update({ indexHierarchyList });
  };

  const showLegalViewDetailsPage = (id: number) => {
    return _application.navigator.push({
      pathname: `/legal-views/${id.toString()}/details`
    });
  };

  const updateLegalView = async (legalView: any) => {
    let elementList =
      legalView.organisationIndexList &&
      legalView.organisationIndexList.length > 0
        ? legalView.organisationIndexList.flat(1)
        : [];
    const { selectedLanguage } = _store.getState<any>();
    let translations = legalView.summaryTranslations.map(
      (summaryTranslation) => {
        var element = SummaryTranslation(summaryTranslation);
        element.organisationId = legalView.organisationId;
        return element;
      }
    );
    let originalSummary = translations?.find(
      (summaryTranslation) =>
        summaryTranslation.languageId === selectedLanguage.id
    );
    originalSummary = originalSummary ? originalSummary : translations[0];
    legalView.summary = originalSummary?.summaryTranslation ?? "";
    legalView.note = originalSummary?.noteTranslation ?? "";
    legalView.summaryTranslations = translations;
    await _application.container
      .resolve<UpdateLegalViewInteractor>("updateLegalView")
      .execute(
        legalView,
        elementList.filter(
          (element) => element !== null && element !== undefined
        )
      );

    _application.container
      .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
      .execute(translate("editors.project_member.create_success_message"));
    showLegalViewDetailsPage(legalView.id);
  };
  const getOrganisationLegalViewTypes = async (organisationId: number) => {
    loader.start("legalViewLoader");
    let legalViewTypes = await _application.container
      .resolve<GetOrganisationLegalViewTypesInteractor>(
        "getOrganisationLegalViewTypes"
      )
      .execute(organisationId);
    _store.update({
      legalViewTypes
    });
    if (legalViewTypes.length === 1) {
      setLegalViewTypeId(legalViewTypes[0].id);
    }
    loader.stop("legalViewLoader");
  };
  const setLegalViewTypeId = async (legalViewTypeId: number) => {
    await getLegalViewTypeIndexes(legalViewTypeId);
    _store.update({
      legalViewTypeId: legalViewTypeId
    });
  };

  const setOrganisationId = async (organisationId: number) => {
    _store.update({ selectedOrganisationId: organisationId });
    await getOrganisationLegalViewTypes(organisationId);
  };
  const getLegalViewTypeIndexes = async (legalViewTypeId: number) => {
    const { user } = _store.getState<any>();
    let organisationIndexes = await _application.container
      .resolve<GetLegalViewTypeIndexDataInteractor>("getLegalViewTypeIndexData")
      .execute(legalViewTypeId, user.organisationId);
    loadIndexData(organisationIndexes);
  };
  const loadIndexData = (organisationIndexes: IDocumentTypeIndexCategory[]) => {
    const { legalViewIndexes } = _store.getState<TLegalViewUpdatePresenter>();
    const { selectedLanguage } = _store.getState<any>();
    var selectedIndexByCategories: IOrganisationIndex[][] = [];
    organisationIndexes.forEach((item) => {
      const result = legalViewIndexes.filter((o) =>
        item.organisationIndexList?.some(({ id }) => o.id === id)
      );
      selectedIndexByCategories.push(result);
    });
    _store.update({
      organisationIndexes: organisationIndexes.map((item) =>
        DocumentTypeIndexCategoryPresentationModel(item, selectedLanguage)
      ),
      selectedIndex: selectedIndexByCategories
    });
  };

  const createAttachment = async (attachment: any) => {
    loader.start("legalViewLoader");
    const {
      uploadedFile,
      anonimisedUploadedFile,
      attachmentTypes,
      legalView,
      attachmentContentTypes
    } = _store.getState<TLegalViewUpdatePresenter>();
    loader.start("legalViewLoader");
    attachment.id =
      attachment.identificationCode === "Y" ? undefined : attachment.id;
    if (uploadedFile) {
      attachment.content = uploadedFile.name;
    }
    const attachmentType = attachmentTypes?.find(
      (value) => value.id === attachment.attachmentTypeId
    );
    attachment.file = uploadedFile;
    let contentType = attachmentContentTypes.find(
      (ct) => ct.id === attachmentType?.attachmentContentTypeId
    );
    if (
      contentType?.code === ContentTypes.FILE_CONTENT_TYPE &&
      attachment.file &&
      !attachment.file?.isDummy
    ) {
      if (anonimisedUploadedFile && !anonimisedUploadedFile.isDummy) {
        attachment.anonymised = false;
        let createdAttachment = await _application.container
          .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
          .execute(
            attachment,
            attachment.file,
            undefined,
            undefined,
            legalView.id
          );
        createdAttachment.anonymised = true;
        await _application.container
          .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
          .execute(
            createdAttachment,
            anonimisedUploadedFile,
            undefined,
            undefined,
            legalView.id
          );
      } else {
        await _application.container
          .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
          .execute(
            attachment,
            attachment.file,
            undefined,
            undefined,
            legalView.id
          );
      }
    } else {
      await _application.container
        .resolve<CreateAttachmentInteractor>("createAttachment")
        .execute(attachment, undefined, undefined, legalView.id);
    }
    let attachments = await _application.container
      .resolve<GetAttachmentsForLegalViewsInteractor>(
        "getAttachmentsForLegalViews"
      )
      .execute(legalView.id);
    loadAttachments(attachments);
    _store.update({
      uploadedFile: undefined,
      anonimisedUploadedFile: undefined
    });
    loader.stop("legalViewLoader");
    _application.container
      .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
      .execute(translate("editors.project.create_success_message"));
    return true;
  };

  const removeAttachment = async (id: number) => {
    const { attachmentList, legalView } =
      _store.getState<TLegalViewUpdatePresenter>();
    loader.start("legalViewLoader");
    var filteredAttachments = attachmentList.filter((item) => item.id !== id);
    await _application.container
      .resolve<RemoveLegalViewAttachmentInteractor>("removeLegalViewAttachment")
      .execute(legalView.id, filteredAttachments);
    let attachments = await _application.container
      .resolve<GetAttachmentsForLegalViewsInteractor>(
        "getAttachmentsForLegalViews"
      )
      .execute(legalView.id);
    loadAttachments(attachments);
    _application.container
      .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
      .execute(translate("editors.project.create_success_message"));
    loader.stop("legalViewLoader");
  };

  const setFileValue = (file: any) => {
    _store.update({ uploadedFile: file });
  };

  const updateAttachment = async (attachment: any) => {
    const {
      uploadedFile,
      anonimisedUploadedFile,
      attachmentTypes,
      attachmentContentTypes,
      legalView
    } = _store.getState<TLegalViewUpdatePresenter>();
    loader.start("legalViewLoader");
    const attachmentType = attachmentTypes?.find(
      (value) => value.id === attachment.attachmentTypeId
    );
    attachment.file = uploadedFile;
    let contentType = attachmentContentTypes.find(
      (ct) => ct.id === attachmentType?.attachmentContentTypeId
    );
    if (
      contentType?.code === ContentTypes.FILE_CONTENT_TYPE &&
      attachment.file &&
      !attachment.file?.isDummy
    ) {
      let createdAttachment = await _application.container
        .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
        .execute(
          attachment,
          attachment.file,
          undefined,
          undefined,
          legalView.id
        );
      if (anonimisedUploadedFile) {
        createdAttachment.anonymised = true;

        await _application.container
          .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
          .execute(
            createdAttachment,
            anonimisedUploadedFile,
            undefined,
            undefined,
            legalView.id
          );
      }
    } else {
      let createdAttachment = await _application.container
        .resolve<CreateAttachmentInteractor>("createAttachment")
        .execute(attachment, undefined, undefined, legalView.id);
      if (anonimisedUploadedFile && !anonimisedUploadedFile.isDummy) {
        createdAttachment.anonymised = true;
        await _application.container
          .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
          .execute(
            createdAttachment,
            anonimisedUploadedFile,
            undefined,
            undefined,
            legalView.id
          );
      }
    }
    let attachments = await _application.container
      .resolve<GetAttachmentsForLegalViewsInteractor>(
        "getAttachmentsForLegalViews"
      )
      .execute(legalView.id);
    loadAttachments(attachments);
    _store.update({
      uploadedFile: undefined,
      anonimisedUploadedFile: undefined
    });
    setFileValue(undefined);
    _application.container
      .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
      .execute(translate("editors.project.create_success_message"));
    loader.stop("legalViewLoader");
    return true;
  };

  const setAttachmentType = async (attachmentType: string) => {
    _store.update({ attachmentType });
  };

  const onUpdate = async (legalView: ILegalView) => {
    getAttachments(legalView);
    getAttachmentTypes(legalView);
  };

  const getAttachmentTypes = async (legalView: ILegalView) => {
    const { attachmentContentTypes } =
      _store.getState<TLegalViewUpdatePresenter>();
    let list = await _application.container
      .resolve<GetAllOrganisationAttachmentTypesInteractor>(
        "getAllOrganisationAttachmentTypes"
      )
      .execute(legalView.organisationId);
    let attachmentTypes = (list || []).map((item) => {
      const contentType = attachmentContentTypes?.find(
        (value) => value.id === item.attachmentContentTypeId
      );
      return AttachmentTypePresentationModel(item, contentType);
    });
    _store.update({ attachmentTypes });
  };

  const getAttachments = async (legalView: ILegalView) => {
    let list = await _application.container
      .resolve<GetAttachmentsForLegalViewsInteractor>(
        "getAttachmentsForLegalViews"
      )
      .execute(legalView.id);
    let attachmentList: TAttachmentPresentationModel[];
    attachmentList = list.map((item) => {
      let model = AttachmentPresentationModel(item);
      return model;
    });
    _store.update({ attachmentList });
  };

  const canEdit = (organisationId) => {
    const { user } = _store.getState<TLegalViewUpdatePresenter>();
    if (user) {
      return user.canEdit(organisationId);
    } else {
      return false;
    }
  };

  const loadLegalViewTypeIndexes = (
    organisationIndexes: IDocumentTypeIndexCategory[]
  ) => {
    const { selectedLanguage, caseDecisionIndexes } = _store.getState<any>();
    var selectedIndexByCategories: IOrganisationIndex[][] = [];
    organisationIndexes.forEach((item) => {
      const result = caseDecisionIndexes.filter((o) =>
        item.organisationIndexList?.some(({ id }) => o.id === id)
      );
      selectedIndexByCategories.push(result);
    });
    _store.update({
      organisationIndexes: organisationIndexes.map((item) =>
        DocumentTypeIndexCategoryPresentationModel(item, selectedLanguage)
      ),
      selectedIndex: selectedIndexByCategories
    });
    var selectedIndexes = selectedIndexByCategories;
    let selectedParentGroupIdList: number[] = [];
    selectedParentGroupIdList.length = selectedIndexes.length;
    selectedIndexes.forEach((selectedIndex, index) => {
      selectedIndex.forEach((element) => {
        const selectedParentGroupId = element?.groups.find(
          (group) => group.parentId == null
        )?.id;
        selectedParentGroupIdList[index] = selectedParentGroupId ?? NaN;
      });
    });

    let selectedSecondGroupIdList: number[] = [];
    selectedSecondGroupIdList.length = selectedIndexes.length;
    selectedIndexes.forEach((selectedIndex, index) => {
      selectedIndex.forEach((element) => {
        const selectedSecondGroupId = element?.groups.find(
          (group) => group.parentId === selectedParentGroupIdList[index]
        )?.id;
        selectedSecondGroupIdList[index] = selectedSecondGroupId ?? NaN;
      });
    });

    let parentGroupList: TOrganisationIndexGroupPresentationModel[][] = [];
    let secondGroupList: TOrganisationIndexGroupPresentationModel[][] = [];
    organisationIndexes.forEach((item) => {
      let parentList: TOrganisationIndexGroupPresentationModel[] = [];
      let secondList: TOrganisationIndexGroupPresentationModel[] = [];
      let indexList = item.organisationIndexList?.map((element) =>
        OrganisationIndexPresentationModel(
          element,
          undefined,
          undefined,
          undefined,
          undefined,
          selectedLanguage
        )
      );
      indexList?.forEach((index) => {
        var parentGroup = index.groups.find((group) => group.parentId == null);
        var secondGroup = index.groups.find(
          (group) => group.parentId === parentGroup?.id
        );
        if (
          parentGroup &&
          parentList.findIndex((element) => element.id === parentGroup?.id) ===
            -1
        ) {
          parentList?.push(parentGroup);
        }

        if (
          secondGroup &&
          secondList.findIndex((element) => element.id === secondGroup?.id) ===
            -1
        ) {
          secondList.push(secondGroup);
        }
      });
      parentGroupList.push(parentList);
      secondGroupList.push(secondList);
    });
    _store.update({
      organisationIndexes,
      parentGroupList,
      secondGroupList,
      selectedParentGroupIdList,
      selectedSecondGroupIdList
    });
  };

  const setLegalViewTypeCode = async (legalViewTypeId: number) => {
    await getLegalViewTypeIndexes(legalViewTypeId);
    _store.update({
      legalViewTypeCode: legalViewTypeId
    });
  };

  const setAnonymisedFileValue = (file: any) => {
    _store.update({ anonimisedUploadedFile: file });
  };

  const setParentGroupId = async (
    selectedParentGroupId: number,
    index: number
  ) => {
    let { selectedParentGroupIdList } =
      _store.getState<TLegalViewUpdatePresenter>();
    selectedParentGroupIdList[index] = selectedParentGroupId;
    _store.update({
      selectedParentGroupIdList
    });
  };
  const setSecondGroupId = async (
    selectedSecondGroupId: number,
    index: number
  ) => {
    let { selectedSecondGroupIdList } =
      _store.getState<TLegalViewUpdatePresenter>();
    selectedSecondGroupIdList[index] = selectedSecondGroupId;
    _store.update({
      selectedSecondGroupIdList
    });
  };

  const publishLegalView = async () => {
    const { legalView } = _store.getState<TLegalViewUpdatePresenter>();

    await _application.container
      .resolve<PublishLegalViewInteractor>("publishLegalView")
      .execute(legalView.id);
  };
  const archiveLegalView = async () => {
    const { legalView } = _store.getState<TLegalViewUpdatePresenter>();
    await _application.container
      .resolve<ArchiveLegalViewInteractor>("archiveLegalView")
      .execute(legalView.id);
  };

  const openIndexDataModal = async (
    index: IOrganisationIndex,
    selectedIds: number[]
  ) => {
    let { previousSelectedIds } = _store.getState<TLegalViewUpdatePresenter>();
    if (
      previousSelectedIds &&
      selectedIds.length < previousSelectedIds?.length
    ) {
      _store.update({
        previousSelectedIds: selectedIds
      });
      return;
    }
    if (index?.indexTypeId) {
      let indexTypeAttributes = await _application.container
        .resolve<GetAllIndexTypeAttributesInteractor>(
          "getAllIndexTypeAttributes"
        )
        .execute(index?.indexTypeId);
      let indexAttributeDataList = await _application.container
        .resolve<GetIndexAttributeDataByIndexInteractor>(
          "getIndexAttributeDataByIndex"
        )
        .execute(index?.id);
      _store.update({
        indexTypeAttributes,
        indexAttributeDataList,
        indexDataModalVisible: true,
        selectedIndexForAttribute: index,
        previousSelectedIds: selectedIds
      });
    }
  };

  const closeIndexDataModal = () => {
    _store.update({
      indexDataModalVisible: false
    });
  };

  const setAttributesData = (
    indexTypeAttribute: IIndexTypeAttribute,
    attributeData: string
  ) => {
    loader.start("caseLawDocumentLoader");
    const { indexAttributeDataList, selectedIndexForAttribute } =
      _store.getState<TLegalViewUpdatePresenter>();
    let indexAttributeData = {
      indexId: selectedIndexForAttribute?.id,
      indexTypeAttributeId: indexTypeAttribute?.id,
      attributeData: attributeData,
      valid: true
    };
    let model = IndexAttributeData(indexAttributeData);
    let existingAttributeData = indexAttributeDataList?.find(
      (item) => item.indexTypeAttributeId === model.indexTypeAttributeId
    );
    if (existingAttributeData) {
      let index = indexAttributeDataList?.indexOf(existingAttributeData);
      if (indexAttributeDataList && index !== undefined) {
        indexAttributeDataList[index].attributeData = attributeData;
      }
    } else {
      indexAttributeDataList?.push(model);
    }
    let filteredList = indexAttributeDataList?.filter(
      (d) => d.attributeData && d.attributeData !== ""
    );
    _store.update({
      indexAttributeData: model,
      indexAttributeDataList: filteredList
    });
    loader.stop("caseLawDocumentLoader");
  };

  const saveAttributeData = async () => {
    loader.start("caseLawDocumentLoader");
    const { indexAttributeDataList, indexTypeAttributes } =
      _store.getState<TLegalViewUpdatePresenter>();
    let invalidTypes: IIndexTypeAttribute[] = [];
    indexTypeAttributes?.forEach(async (type) => {
      let existingData = indexAttributeDataList?.filter(
        (d) => d.indexTypeAttributeId === type.id && d.attributeData
      );
      if (existingData?.length === 0 && type.required) {
        invalidTypes.push(type);
      }
    });
    if (invalidTypes.length > 0) {
      await _application.container
        .resolve<ShowWarningMessageInteractor>("showWarningMessage")
        .execute(translate("editors.case_law_documents.required_data"));
      loader.stop("indexTypeLoader");
      return;
    }
    indexAttributeDataList &&
      (await _application.container
        .resolve<CreateIndexAttributeDataInteractor>("createIndexAttributeData")
        .execute(indexAttributeDataList));
    closeIndexDataModal();
    _application.container
      .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
      .execute(translate("editors.project.create_success_message"));
    _store.update({ indexAttributeDataList: [] });
    loader.stop("caseLawDocumentLoader");
  };

  return {
    ...state,
    store: _store,
    loader,
    application: _application,
    translate,
    loadLegalView,
    loadReferenceValues,
    loadLegalViewIndexes,
    showLegalViewDetailsPage,
    updateLegalView,
    setLegalViewTypeId,
    setOrganisationId,
    setAttachmentType,
    onUpdate,
    updateAttachment,
    createAttachment,
    setFileValue,
    getAttachments,
    loadAttachments,
    canEdit,
    publishLegalView,
    archiveLegalView,
    setParentGroupId,
    setSecondGroupId,
    setLegalViewTypeCode,
    setAnonymisedFileValue,
    removeAttachment,
    setAttributesData,
    openIndexDataModal,
    closeIndexDataModal,
    saveAttributeData
  };
}, defaultState);

export default LegalViewUpdatePresenter;
